<template>
    <v-row justify="center">
        <PlantConfigDialog
            :dialog.sync="configPlantDialog"
            :plantNodeId="
                configingPlant && configingPlant.node
                    ? configingPlant.node.id
                    : null
            "
            v-on:item-added="onPlantConfirmed"
        ></PlantConfigDialog>

        <v-dialog v-model="dialog" persistent max-width="1100px" scrollable>
            <v-card
                :disabled="isLoadingPlannedTask"
                :loading="isLoadingPlannedTask ? 'white' : null"
            >
                <v-card-title class="primary white--text">
                    {{ DialogTitle }}

                    <!-- PlanFolder Dropdown list -->
                    <div width="60px" class="ml-10">
                        <v-select
                            return-object
                            dense
                            hide-details
                            solo
                            hint="Select a target folder"
                            prefix="Folder: "
                            :items="dropdownValue"
                            item-value="node.id"
                            item-text="node.name"
                            v-model="targetFolderToCopy"
                        ></v-select>
                    </div>

                    <v-spacer></v-spacer>

                    <v-btn icon dark @click="closeMainDialog">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-0">
                    <v-stepper v-model="step" vertical>
                        <!-- Step 1: select plant -->
                        <v-stepper-step step="1" :complete="step > 1">
                            <span v-if="selectedPlant === null"
                                >Select a plant</span
                            >
                            <span
                                v-if="selectedPlant"
                                class="grey--text text--darken-4 mb-2"
                                >{{
                                    selectedPlant && selectedPlant.plant
                                        ? selectedPlant.plant.name
                                        : ""
                                }}</span
                            >
                            <v-row v-if="selectedPlant" class="px-2">
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>straighten</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant && selectedPlant.potSize
                                            ? selectedPlant.potSize.potSizeCm
                                            : "-"
                                    }}cm
                                </v-chip>
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>color_lens</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant && selectedPlant.color
                                            ? selectedPlant.color.color
                                            : "-"
                                    }}
                                </v-chip>
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>blur_on</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant &&
                                        selectedPlant.environment
                                            ? selectedPlant.environment
                                                  .environment
                                            : "-"
                                    }}
                                </v-chip>
                                <v-chip class="mr-1 mb-1" small>
                                    <v-avatar left>
                                        <v-icon>turned_in_not</v-icon>
                                    </v-avatar>
                                    {{
                                        selectedPlant && selectedPlant.tag
                                            ? selectedPlant.tag.tag
                                            : "-"
                                    }}
                                </v-chip>
                                <v-spacer />
                            </v-row>
                        </v-stepper-step>

                        <v-stepper-content step="1">
                            <v-form ref="formStep1">
                                <v-card flat>
                                    <!-- Choices table -->
                                    <v-row>
                                        <!-- Header -->
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="search"
                                                append-outer-icon="search"
                                                label="Search"
                                                clearable
                                                hide-details
                                                @keydown.enter="
                                                    refreshCurrentPlantsPage()
                                                "
                                                @click:append-outer="
                                                    refreshCurrentPlantsPage()
                                                "
                                                @click:clear="onClearSearch()"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-data-table
                                                :headers="headers"
                                                :loading="
                                                    $apollo.queries.tenantPlants
                                                        .loading
                                                "
                                                :items="tenantPlants.edges"
                                                :options.sync="dataTableOptions"
                                                loading-text="Loading... Please wait"
                                                :server-items-length="
                                                    tenantPlants
                                                        ? tenantPlants.totalCount
                                                        : 0
                                                "
                                                :items-per-page="
                                                    defaultItemsPerPage
                                                "
                                                :footer-props="{
                                                    'items-per-page-options': [
                                                        10,
                                                        25,
                                                        50,
                                                        75,
                                                        100
                                                    ],
                                                    'disable-pagination':
                                                        $apollo.queries
                                                            .tenantPlants
                                                            .loading
                                                }"
                                            >
                                                <template
                                                    v-slot:item.node.numProducts="{
                                                        item
                                                    }"
                                                >
                                                    <span
                                                        :class="
                                                            item.node
                                                                .numProducts <=
                                                            0
                                                                ? 'red--text'
                                                                : ''
                                                        "
                                                        >{{
                                                            item.node
                                                                .numProducts
                                                        }}</span
                                                    >
                                                </template>
                                                <template
                                                    v-slot:item.action="{
                                                        item
                                                    }"
                                                >
                                                    <v-btn
                                                        small
                                                        outlined
                                                        rounded
                                                        color="primary"
                                                        @click="
                                                            onConfigPlant(item)
                                                        "
                                                        >Select</v-btn
                                                    >
                                                    <v-btn
                                                        icon
                                                        color="primary"
                                                        @click="
                                                            goToPlantDetail(
                                                                item.node.id
                                                            )
                                                        "
                                                    >
                                                        <v-icon>launch</v-icon>
                                                    </v-btn>
                                                </template>
                                            </v-data-table>
                                        </v-col>
                                    </v-row>
                                </v-card>
                            </v-form>
                        </v-stepper-content>

                        <!-- Step 2: quantity and supplier -->
                        <v-stepper-step
                            step="2"
                            :editable="step > 2"
                            :complete="step > 2"
                        >
                            Quantity and supplier
                            <span
                                v-if="step > 2"
                                class="mt-1 grey--text text--darken-2 subtitle-2"
                                >{{ quantity }} plants</span
                            >
                        </v-stepper-step>

                        <v-stepper-content step="2">
                            <v-form ref="formStep2">
                                <v-card flat>
                                    <v-row class="align-center">
                                        <v-col cols="12" sm="4">
                                            <v-text-field
                                                v-model="quantity"
                                                label="Quantity*"
                                                :rules="[rules.required]"
                                                min="1"
                                                type="number"
                                                outlined
                                                class="subtitle-2 mb-n5"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-autocomplete
                                                v-model="supplierNodeId"
                                                :items="suppliers.edges"
                                                item-text="node.name"
                                                item-value="node.id"
                                                :loading="
                                                    $apollo.queries.suppliers
                                                        .loading
                                                "
                                                label="Supplier"
                                                class="subtitle-2 mb-n5"
                                                outlined
                                                clearable
                                            ></v-autocomplete>
                                        </v-col>
                                        <v-col cols="12" sm="4">
                                            <v-text-field
                                                v-model="supplierOrderNo"
                                                label="Supplier Order No"
                                                outlined
                                                class="subtitle-2 mb-n5"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12">
                                            <v-text-field
                                                v-model="note"
                                                label="Note"
                                                outlined
                                                class="subtitle-2 mb-n5"
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>
                                </v-card>

                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn
                                        text
                                        rounded
                                        @click="
                                            step = 1;
                                            selectedPlant = null;
                                        "
                                        >Previous</v-btn
                                    >
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        @click="step2Next"
                                        >Continue</v-btn
                                    >
                                </v-card-actions>
                            </v-form>
                        </v-stepper-content>

                        <!-- Step 3: growth phases -->
                        <v-stepper-step
                            step="3"
                            :editable="step > 3"
                            :complete="step > 3"
                        >
                            Phases
                            <span
                                v-if="step > 3"
                                class="mt-1 grey--text text--darken-2 subtitle-2"
                                >{{ plannedTask.numPhases }} phases,
                                {{ totalGrowthDays }} days</span
                            >
                        </v-stepper-step>

                        <v-stepper-content step="3">
                            <v-form ref="formStep3">
                                <v-card flat>
                                    <v-row class="align-center">
                                        <v-col cols="12" sm="3" class="ml-3">
                                            <v-switch
                                                v-model="
                                                    isImportPhasesFromTemplate
                                                "
                                                label="Import from template"
                                            ></v-switch>
                                        </v-col>

                                        <v-spacer />
                                        <!-- Number of Phases -->
                                        <v-col
                                            cols="7"
                                            sm="4"
                                            v-if="!isImportPhasesFromTemplate"
                                        >
                                            <v-select
                                                v-model="plannedTask.numPhases"
                                                :items="numPhasesChoices"
                                                :rules="[rules.required]"
                                                label="# Phases*"
                                                :disabled="
                                                    selectedTemplateNodeId !==
                                                        null
                                                "
                                                outlined
                                                :menu-props="{ offsetY: true }"
                                                class="subtitle-2 mb-n5"
                                            ></v-select>
                                        </v-col>
                                        <!-- select tempalte -->
                                        <v-col cols="7" sm="4" v-else>
                                            <v-autocomplete
                                                v-model="selectedTemplateNodeId"
                                                :items="
                                                    sortedSelectedPlantGrowthTemplates
                                                "
                                                :rules="[
                                                    isImportPhasesFromTemplate
                                                        ? rules.required
                                                        : null
                                                ]"
                                                item-value="node.id"
                                                item-text="node.name"
                                                label="Template *"
                                                class="subtitle-2 mb-n5"
                                                outlined
                                            >
                                            </v-autocomplete>
                                        </v-col>

                                        <!-- Max Sales Days -->
                                        <v-col cols="5" sm="3">
                                            <v-select
                                                v-model="
                                                    plannedTask.sales.numDays
                                                "
                                                :items="numSaleDaysChoices"
                                                :rules="[rules.required]"
                                                label="#Sale Days*"
                                                outlined
                                                :menu-props="{ offsetY: true }"
                                                class="subtitle-2 mb-n5"
                                            ></v-select>
                                        </v-col>
                                    </v-row>

                                    <!-- Phases -->
                                    <v-row>
                                        <v-card-text
                                            class="pt-0 font-weight-medium"
                                        >
                                            <v-row align="center">
                                                <v-divider></v-divider>
                                                <span class="pl-3 pr-3"
                                                    >Phases</span
                                                >
                                                <v-divider></v-divider>
                                            </v-row>
                                        </v-card-text>
                                    </v-row>

                                    <v-progress-linear
                                        color="primary"
                                        :active="isLoadingPlantGrowthTemplate"
                                        :indeterminate="
                                            isLoadingPlantGrowthTemplate
                                        "
                                        height="2"
                                    ></v-progress-linear>

                                    <v-row
                                        v-for="(phase,
                                        index) in plannedTask.phases"
                                        v-bind:key="index"
                                    >
                                        <v-col cols="3" sm="2">
                                            <v-text-field
                                                v-model="phase.phaseNo"
                                                label="Phase"
                                                outlined
                                                disabled
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="4" sm="2">
                                            <v-text-field
                                                v-model="phase.numDays"
                                                :rules="[rules.required]"
                                                type="number"
                                                min="1"
                                                label="#Days*"
                                                outlined
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="5" sm="2">
                                            <v-text-field
                                                v-model="phase.numPlantsPerM2"
                                                :rules="[rules.required]"
                                                type="number"
                                                min="1"
                                                label="#Plants / m2*"
                                                outlined
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="5" sm="2">
                                            <v-text-field
                                                :value="
                                                    calculateSpaceM2(
                                                        phase.numPlantsPerM2,
                                                        quantity
                                                    )
                                                "
                                                type="number"
                                                label="Space m2*"
                                                outlined
                                                disabled
                                                class="subtitle-2"
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="7" sm="4">
                                            <v-autocomplete
                                                v-model="
                                                    phase.greenhouseDepartmentSectionNodeId
                                                "
                                                :items="
                                                    greenhouseDepartmentSections
                                                "
                                                item-value="id"
                                                item-text="fullName"
                                                :loading="
                                                    $apollo.queries.greenhouses
                                                        .loading
                                                "
                                                :rules="[rules.required]"
                                                label="Greenhouse*"
                                                class="subtitle-2"
                                                outlined
                                            >
                                            </v-autocomplete>
                                        </v-col>
                                    </v-row>
                                    <v-divider></v-divider>
                                </v-card>
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn text rounded @click="step = 2"
                                        >Previous</v-btn
                                    >
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        @click="step3Next"
                                        >Continue</v-btn
                                    >
                                </v-card-actions>
                            </v-form>
                        </v-stepper-content>

                        <!-- Step 4: start date -->
                        <v-stepper-step
                            step="4"
                            :editable="step > 4"
                            :complete="step > 4"
                            >Select start date</v-stepper-step
                        >

                        <v-stepper-content step="4" class="pt-0 pl-0">
                            <v-form ref="formStep4">
                                <v-card-text class="pt-0">
                                    <v-row class="mx-0">
                                        <v-col cols="12" class="pb-0">
                                            <v-radio-group
                                                v-model="startDateOption"
                                                row
                                            >
                                                <v-radio
                                                    label="Growth Start Date"
                                                    value="growth"
                                                    color="success"
                                                ></v-radio>
                                                <v-radio
                                                    label="Sales Start Date"
                                                    value="sales"
                                                    color="warning"
                                                ></v-radio>
                                            </v-radio-group>
                                        </v-col>
                                    </v-row>
                                    <v-row class="mx-0">
                                        <v-col cols="12" md="5" class="">
                                            <v-date-picker
                                                v-model="selectedStartDate"
                                                :allowed-dates="
                                                    functionAllowedDates
                                                "
                                                :events="functionCalanderEvents"
                                                first-day-of-week="1"
                                                locale-first-day-of-year="4"
                                                full-width
                                                show-week
                                                :show-current="
                                                    datePickerCurrentDate
                                                "
                                            ></v-date-picker>
                                        </v-col>
                                        <v-col cols="12" md="7" class="pt-0">
                                            <v-row class="mx-0 mt-3">
                                                <v-col cols="12" sm="6" md="6">
                                                    <!-- Growing period -->
                                                    <div class="my-0">
                                                        <p
                                                            v-if="
                                                                plannedTask.startDate !==
                                                                    null
                                                            "
                                                            class="my-0"
                                                        >
                                                            <v-icon
                                                                small
                                                                color="green lighten-1"
                                                                class="mr-1"
                                                                >fiber_manual_record</v-icon
                                                            >
                                                            <span class="mr-1">
                                                                Growth:
                                                            </span>
                                                            {{
                                                                growthStartDate
                                                                    | moment(
                                                                        "YYYY"
                                                                    )
                                                            }}-<strong
                                                                >w{{
                                                                    growthStartDate
                                                                        | moment(
                                                                            "w"
                                                                        )
                                                                }}</strong
                                                            >
                                                            ~
                                                            {{
                                                                growthEndDate
                                                                    | moment(
                                                                        "YYYY"
                                                                    )
                                                            }}-<strong
                                                                >w{{
                                                                    growthEndDate
                                                                        | moment(
                                                                            "w"
                                                                        )
                                                                }}</strong
                                                            >
                                                        </p>
                                                        <p v-else class="my-0">
                                                            <v-icon
                                                                small
                                                                color="green lighten-1"
                                                                class="mr-1"
                                                                >fiber_manual_record</v-icon
                                                            >
                                                            <span class="mr-1">
                                                                Growth:
                                                            </span>
                                                            <span
                                                                class="error--text"
                                                            >
                                                                Please select
                                                                start
                                                                date!</span
                                                            >
                                                        </p>
                                                    </div>
                                                </v-col>
                                                <v-col cols="12" sm="6" md="6">
                                                    <!-- Sales period -->
                                                    <div class="my-0">
                                                        <p
                                                            v-if="
                                                                plannedTask.startDate !==
                                                                    null
                                                            "
                                                            class="my-0"
                                                        >
                                                            <v-icon
                                                                small
                                                                color="orange"
                                                                class="mr-1"
                                                                >fiber_manual_record</v-icon
                                                            >
                                                            <span class="mr-2">
                                                                Sales:
                                                            </span>
                                                            {{
                                                                salesStartDate
                                                                    | moment(
                                                                        "YYYY"
                                                                    )
                                                            }}-<strong
                                                                >w{{
                                                                    salesStartDate
                                                                        | moment(
                                                                            "w"
                                                                        )
                                                                }}</strong
                                                            >
                                                            ~
                                                            {{
                                                                salesEndDate
                                                                    | moment(
                                                                        "YYYY"
                                                                    )
                                                            }}-<strong
                                                                >w{{
                                                                    salesEndDate
                                                                        | moment(
                                                                            "w"
                                                                        )
                                                                }}</strong
                                                            >
                                                        </p>
                                                        <p v-else class="my-0">
                                                            <v-icon
                                                                small
                                                                color="orange"
                                                                class="mr-1"
                                                                >fiber_manual_record</v-icon
                                                            >
                                                            <span class="mr-1">
                                                                Sales:
                                                            </span>
                                                            <span
                                                                class="error--text"
                                                            >
                                                                Please select
                                                                start
                                                                date!</span
                                                            >
                                                        </p>
                                                    </div>
                                                </v-col>
                                            </v-row>
                                            <v-row class="mx-0">
                                                <v-col
                                                    cols="12"
                                                    class="pb-0 px-0"
                                                >
                                                    <ExpectedSalesPriceChart
                                                        :key="
                                                            salesStartWeek || 0
                                                        "
                                                        :salesStartWeek="
                                                            salesStartWeek
                                                        "
                                                        :salesEndWeek="
                                                            salesEndWeek
                                                        "
                                                        :volumeList="
                                                            salesVolumnList
                                                        "
                                                        :priceList="
                                                            salesPriceList
                                                        "
                                                    ></ExpectedSalesPriceChart>
                                                </v-col>
                                            </v-row>
                                        </v-col>
                                    </v-row>

                                    <v-divider class="my-3"></v-divider>

                                    <v-row class="mx-0">
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                v-model="quantity"
                                                label="Input Quantity*"
                                                :rules="[rules.required]"
                                                min="1"
                                                type="number"
                                                outlined
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                v-model="failureRate"
                                                suffix="%"
                                                label="Failure Rate*"
                                                type="number"
                                                :rules="[rules.required]"
                                                min="0"
                                                max="100"
                                                outlined
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-text-field
                                                :value="
                                                    (
                                                        ((quantity || 0) *
                                                            (100 -
                                                                (failureRate ||
                                                                    0))) /
                                                        100
                                                    ).toFixed(0)
                                                "
                                                label="Output Quantity*"
                                                type="number"
                                                disabled
                                                outlined
                                                hide-details
                                            ></v-text-field>
                                        </v-col>
                                    </v-row>

                                    <v-row class="ma-0">
                                        <!-- Cost -->
                                        <v-col
                                            cols="12"
                                            md="6"
                                            class="d-flex flex-column"
                                        >
                                            <v-card
                                                outlined
                                                class="flex d-flex flex-column"
                                            >
                                                <v-card-title>
                                                    Cost
                                                </v-card-title>
                                                <v-card-text class="flex">
                                                    <v-row class="ma-0">
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                v-model="
                                                                    unitPurchasePrice
                                                                "
                                                                prefix="€"
                                                                label="Young Plant Price (unit)"
                                                                type="number"
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="
                                                                    calYoungPlantCost.toFixed(
                                                                        0
                                                                    )
                                                                "
                                                                prefix="€"
                                                                label="Young Plant Cost"
                                                                type="number"
                                                                disabled
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                v-model="
                                                                    plantFixedCostRate
                                                                "
                                                                prefix="€"
                                                                label="Growth Cost (unit)"
                                                                type="number"
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="
                                                                    calGrowthCost.toFixed(
                                                                        0
                                                                    )
                                                                "
                                                                prefix="€"
                                                                label="Growth Cost"
                                                                type="number"
                                                                disabled
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                v-model="
                                                                    productFixedCostRate
                                                                "
                                                                prefix="€"
                                                                label="Product Cost (unit)"
                                                                type="number"
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="
                                                                    calProductCost.toFixed(
                                                                        0
                                                                    )
                                                                "
                                                                prefix="€"
                                                                label="Product Cost"
                                                                type="number"
                                                                disabled
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col cols="6">
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="
                                                                    calTotalOperatingCost.toFixed(
                                                                        0
                                                                    )
                                                                "
                                                                prefix="€"
                                                                label="Business Cost"
                                                                type="number"
                                                                disabled
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                    </v-row>
                                                </v-card-text>
                                            </v-card>
                                        </v-col>

                                        <!-- Revenue -->
                                        <v-col
                                            cols="12"
                                            md="6"
                                            class="d-flex flex-column"
                                        >
                                            <v-card
                                                outlined
                                                class="flex d-flex flex-column"
                                            >
                                                <v-card-title>
                                                    Revenue
                                                </v-card-title>
                                                <v-card-text class="flex">
                                                    <v-row class="ma-0">
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                v-model="
                                                                    expectedSalesPrice
                                                                "
                                                                prefix="€"
                                                                label="Expected Sales Price (unit)"
                                                                type="number"
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                        <v-col cols="6">
                                                            <v-text-field
                                                                :value="
                                                                    calEstimatedRevenue.toFixed(
                                                                        0
                                                                    )
                                                                "
                                                                prefix="€"
                                                                label="Total Revenue"
                                                                type="number"
                                                                disabled
                                                                outlined
                                                                hide-details
                                                            ></v-text-field>
                                                        </v-col>
                                                    </v-row>
                                                </v-card-text>
                                                <v-card-title>
                                                    Profit
                                                    <v-spacer></v-spacer>€{{
                                                        calProfit.toFixed(0)
                                                    }}
                                                </v-card-title>
                                            </v-card>
                                        </v-col>
                                    </v-row>
                                </v-card-text>

                                <v-card-actions
                                    v-if="selectedPlant !== null"
                                    class="mt-0 justify-center"
                                >
                                    <v-btn
                                        outlined
                                        rounded
                                        :disabled="isSaving"
                                        @click="step = 3"
                                        >Previous</v-btn
                                    >
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        :loading="isSaving"
                                        :disabled="
                                            plannedTask.startDate === null
                                        "
                                        @click="saveMutation"
                                        >Save</v-btn
                                    >
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        :loading="isSaving"
                                        :disabled="
                                            plannedTask.startDate === null
                                        "
                                        @click="saveMutationAndAddAnother"
                                        >Save and Add Another</v-btn
                                    >
                                </v-card-actions>
                                <!-- <v-card-actions v-if="selectedPlant!==null">
                                    <v-spacer />
                                    <v-btn text rounded @click="step=3">Previous</v-btn>
                                    <v-btn
                                        outlined
                                        rounded
                                        color="primary"
                                        :loading="isSaving"
                                        :disabled="plannedTask.startDate === null"
                                        @click="save"
                                    >Save</v-btn>
                                </v-card-actions>-->
                            </v-form>
                        </v-stepper-content>
                    </v-stepper>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import moment from "moment";
import PlantConfigDialog from "@/components/plants/PlantConfigDialog.vue";
import ExpectedSalesPriceChart from "@/components/planner/ExpectedSalesPriceChart.vue";
// import { parse } from "graphql";

export default {
    name: "planned-task-editor",
    props: [
        "dialog",
        "PlanFolderID",
        "dropdownValue",
        "DialogTitle",
        "currentlySelectedFolder",
        "createOrCopyPlanToFolderBool"
    ],
    components: {
        PlantConfigDialog,
        ExpectedSalesPriceChart
    },

    apollo: {
        tenantPlants: {
            query: gql`
                query tenantPlants(
                    $first: Int
                    $last: Int
                    $before: String
                    $after: String
                    $orderBy: String
                    $search: String
                    $isArchived: Boolean
                ) {
                    tenantPlants(
                        first: $first
                        last: $last
                        before: $before
                        after: $after
                        search: $search
                        isArchived: $isArchived
                        orderBy: $orderBy
                    ) {
                        totalCount
                        pageInfo {
                            startCursor
                            endCursor
                        }
                        edges {
                            node {
                                id
                                name
                                plantSpecies {
                                    id
                                    name
                                }
                                numProducts
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    first: 10
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.tenantPlants,
            skip() {
                return true;
            }

            // pollInterval: 300 // ms
        },

        tenantOperatingCostRates: {
            query: gql`
                query tenantOperatingCostRates {
                    tenantOperatingCostRates {
                        edges {
                            node {
                                id
                                month
                                greenhouse {
                                    id
                                }
                                costPerM2Day
                            }
                        }
                    }
                }
            `,
            variables() {
                return {};
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => {
                return data.tenantOperatingCostRates;
            },
            skip() {
                return this.step != 4;
            }
            // pollInterval: 60000 // ms
        },

        suppliers: {
            query: gql`
                query suppliers {
                    suppliers {
                        edges {
                            node {
                                id
                                name
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.suppliers,
            skip() {
                return this.step != 2;
            }
            // pollInterval: 300 // ms
        },

        greenhouses: {
            query: gql`
                query greenhouses {
                    greenhouses {
                        edges {
                            node {
                                id
                                name
                                greenhouseDepartmentSet(isPlaceholder: false) {
                                    edges {
                                        node {
                                            id
                                            name
                                            greenhouseDepartmentSectionSet(
                                                isPlaceholder: false
                                            ) {
                                                edges {
                                                    node {
                                                        id
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: data => data.greenhouses,
            skip() {
                return this.step != 3;
            }
            // pollInterval: 300 // ms
        }
    },

    data: function() {
        return {
            // title: this.PlanFolderID,
            // isLoadingPlants: false,
            // isLoadingSuppliers: false,
            // isLoadingGreenhouseDepartmentSections: false,
            isLoadingPlannedTask: false,
            isLoadingPlantGrowthTemplate: false,
            configPlantDialog: false,
            isSaving: false,
            updatedPlannedTaskNodeId: null,
            isImportPhasesFromTemplate: true,

            // numSaleDaysChoices: [1, 2, 3, 4, 5],
            numPhasesChoices: [1, 2, 3, 4, 5],

            defaultItemsPerPage: 10,
            dataTableOptions: {},
            page: 1,
            tenantPlants: {},
            tenantOperatingCostRates: null,
            targetFolderToCopy: { node: { name: "Default", id: null } },

            step: 1,
            search: null,
            configingPlant: {},
            selectedPlant: null,
            supplierNodeId: null,
            supplierOrderNo: null,
            unitPurchasePrice: null,
            plantFixedCostRate: null,
            failureRate: null,
            note: null,
            selectedTemplateNodeId: null,
            quantity: null,
            // currentGreenhouseDepartmentSectionNodeId: null,
            currentPhaseNo: 1,
            // currentSpaceM2: null,

            // sales volumn and price
            salesStartWeek: null,
            salesEndWeek: null,
            salesRevenueList: [],
            salesVolumnList: [],
            salesPriceList: [],
            expectedSalesPrice: 0,
            productFixedCostRate: 0,

            startDateOption: "sales",
            selectedStartDate: null,

            suppliers: {},
            greenhouses: {},
            plantSalesQuantityAndPriceByWeek: [],
            datePickerCurrentDate: null,

            plannedTask: {
                numPhases: 1,
                startDate: null,
                endDate: null,
                phases: [
                    {
                        isSalesPhase: false,
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        startDate: null,
                        endDate: null
                    }
                ],
                sales: {
                    isSalesPhase: true,
                    numDays: 21
                }
            },

            defaultPlannedTask: {
                numPhases: 1,
                startDate: null,
                endDate: null,
                phases: [
                    {
                        isSalesPhase: false,
                        phaseNo: 1,
                        numDays: null,
                        numPlantsPerM2: null,
                        startDate: null,
                        endDate: null
                    }
                ],
                sales: {
                    isSalesPhase: true,
                    numDays: 21
                }
            },

            headers: [
                {
                    text: "Plant Species",
                    align: "left",
                    sortable: true,
                    value: "node.plantSpecies.name"
                },
                {
                    text: "Plant",
                    align: "left",
                    sortable: true,
                    value: "node.name"
                },
                {
                    text: "#Products",
                    align: "left",
                    sortable: true,
                    value: "node.numProducts"
                },
                {
                    text: "Actions",
                    align: "center",
                    value: "action",
                    sortable: false
                }
            ],
            rules: {
                required: v => !helper.isEmpty(v) || "This field is required",
                listRequired: v =>
                    (v || "").length >= 1 || `This field is required`,
                minLength: len => v =>
                    (v || "").length >= len || `Require at least ${len}`,
                maxLength: len => v => (v || "").length <= len || "Too long"
            }
        };
    },
    computed: {
        weeks() {
            return Array(53)
                .fill(0)
                .map((_, i) => i + 1);
        },
        numSaleDaysChoices: function() {
            return Array(120)
                .fill(0)
                .map((_, i) => i + 1);
        },

        sortedSelectedPlantGrowthTemplates: function() {
            var _sorted = this.selectedPlant
                ? this.selectedPlant.plant.plantGrowthTemplateSet.edges.filter(
                      item => {
                          return (
                              item.node.potSize.id ==
                              this.selectedPlant.potSize.id
                          );
                      }
                  )
                : [];

            if (_sorted.length > 0) {
                _sorted = _sorted.sort((a, b) => {
                    if (a.node.potSize.potSizeCm === b.node.potSize.potSizeCm) {
                        return a.node.name.toLowerCase() ==
                            b.node.name.toLowerCase()
                            ? 0
                            : a.node.name.toLowerCase() >
                              b.node.name.toLowerCase()
                            ? 1
                            : -1;
                    }
                    return a.node.potSize.potSizeCm - b.node.potSize.potSizeCm;
                });
            }

            return _sorted;
        },

        greenhouseDepartmentSections() {
            var _sections = [];

            if (this.greenhouses.edges) {
                for (let greenhouse of this.greenhouses.edges) {
                    if (greenhouse.node.greenhouseDepartmentSet) {
                        for (let department of greenhouse.node
                            .greenhouseDepartmentSet.edges) {
                            if (
                                department.node.greenhouseDepartmentSectionSet
                            ) {
                                for (let section of department.node
                                    .greenhouseDepartmentSectionSet.edges) {
                                    _sections.push({
                                        id: section.node.id,
                                        name: section.node.name,
                                        fullName:
                                            greenhouse.node.name +
                                            "-" +
                                            department.node.name +
                                            "-" +
                                            section.node.name
                                    });
                                }
                            }
                        }
                    }
                }
            }

            // sort by full name
            _sections = _sections.sort((a, b) => {
                return a.fullName.toLowerCase() === b.fullName.toLowerCase
                    ? 0
                    : a.fullName.toLowerCase() > b.fullName.toLowerCase()
                    ? 1
                    : -1;
            });

            return _sections;
        },
        greenhouseWithSections() {
            var _sections = [];

            if (this.greenhouses.edges) {
                for (let greenhouse of this.greenhouses.edges) {
                    if (greenhouse.node.greenhouseDepartmentSet) {
                        for (let department of greenhouse.node
                            .greenhouseDepartmentSet.edges) {
                            if (
                                department.node.greenhouseDepartmentSectionSet
                            ) {
                                for (let section of department.node
                                    .greenhouseDepartmentSectionSet.edges) {
                                    _sections.push({
                                        sectionId: section.node.id,
                                        sectionName: section.node.name,
                                        departmentId: department.node.id,
                                        departmentName: department.node.name,
                                        greenhouseId: greenhouse.node.id,
                                        greenhouseName: greenhouse.node.name
                                    });
                                }
                            }
                        }
                    }
                }
            }

            return _sections;
        },
        totalGrowthDays() {
            return this.plannedTask.phases.reduce(
                (total, item) => parseInt(total) + parseInt(item.numDays || 0),
                0
            );
        },
        growthStartDate() {
            return this.plannedTask.startDate
                ? new Date(this.plannedTask.startDate)
                : null;
        },
        growthEndDate() {
            return this.growthStartDate
                ? new Date(
                      this.growthStartDate.getTime() +
                          (this.totalGrowthDays - 1) * 864e5
                  )
                : null;
        },
        salesStartDate() {
            return this.growthEndDate
                ? new Date(this.growthEndDate.getTime() + 864e5)
                : null;
        },
        salesEndDate() {
            return this.salesStartDate
                ? new Date(
                      this.salesStartDate.getTime() +
                          (this.plannedTask.sales.numDays - 1) * 864e5
                  )
                : null;
        },
        plannedTaskWithSalesPhases() {
            // const today = new Date();

            var _phases = this.plannedTask.phases.map(item => {
                return {
                    ...item,
                    spaceM2: this.calculateSpaceM2(
                        item.numPlantsPerM2,
                        this.quantity
                    )
                };
            });

            // add sales phase: copy growth task's last phase [numPlantsPerM2] and [greenhouseDepartmentSectionNodeId]
            _phases.push({
                ...this.plannedTask.sales,
                phaseNo: _phases.length + 1,
                numPlantsPerM2: _phases[_phases.length - 1].numPlantsPerM2,
                greenhouseDepartmentSectionNodeId:
                    _phases[_phases.length - 1]
                        .greenhouseDepartmentSectionNodeId,
                spaceM2: _phases[_phases.length - 1].spaceM2,
                startDate: moment(this.salesStartDate).format("YYYY-MM-DD"),
                endDate: moment(this.salesEndDate).format("YYYY-MM-DD")
            });

            return _phases;
        },
        currentPhaseColor() {
            const today = new Date();

            if (
                this.plannedTask.startDate == null ||
                moment(this.growthStartDate).isAfter(today, "day")
            ) {
                return "grey";
            }

            return this.currentPhaseNo > this.plannedTask.numPhases
                ? "orange"
                : "success";
        },

        calYoungPlantCost() {
            return (
                parseFloat(this.unitPurchasePrice || 0) *
                parseFloat(this.quantity || 0)
            );
        },

        calGrowthCost() {
            return (
                parseFloat(this.plantFixedCostRate || 0) *
                parseFloat(this.quantity || 0)
            );
        },

        calProductCost() {
            return (
                parseFloat(this.productFixedCostRate || 0) *
                parseFloat(this.quantity || 0) *
                (1 - parseFloat(this.failureRate || 0) / 100)
            );
        },

        calTotalOperatingCost() {
            /* return if parametters are not ready  */
            if (
                !this.quantity ||
                !this.plannedTaskWithSalesPhases ||
                !this.tenantOperatingCostRates ||
                this.step != 4
            ) {
                return 0;
            }

            let _totalOperatingCost = 0;

            /* calculate operating cost */
            if (this.tenantOperatingCostRates.edges.length > 0) {
                // // sort operating cost by month
                // let _operatingCostRateByMonth = [
                //     ...this.tenantOperatingCostRates.edges
                // ]
                //     .sort((a, b) => {
                //         return a.node.month - b.node.month;
                //     })
                //     .map(item => {
                //         return item.node.costPerM2Day;
                //     });

                /* for each phase, calcualte monthly operating cost */
                this.plannedTaskWithSalesPhases.forEach(phase => {
                    let _phaseOperatingCost = 0;

                    // find all months of current phase and days in each month
                    const _month_dict = helper.countDaysInRangeByMonth(
                        phase.startDate,
                        phase.endDate
                    );

                    // console.log("phase: ", phase);
                    // console.log("_month_dict: ", _month_dict);

                    // look up phase greenhouse
                    let _greenhouseDepartmentSection = this.greenhouseWithSections.find(
                        item =>
                            item.sectionId ==
                            phase.greenhouseDepartmentSectionNodeId
                    );

                    // console.log("_greenhouseDepartmentSection: ", _greenhouseDepartmentSection);

                    if (_greenhouseDepartmentSection) {
                        // for each month, get opearting cost rate
                        Object.entries(_month_dict).forEach(([m, days]) => {
                            // console.log("Key: ", k, " Value: ", v);

                            // let _operatingCostRate = _operatingCostRateByMonth[m - 1] || 0;
                            let _operatingCostRate = this.tenantOperatingCostRates.edges.find(
                                item => {
                                    return (
                                        item.node.month == m &&
                                        item.node.greenhouse.id ==
                                            _greenhouseDepartmentSection.greenhouseId
                                    );
                                }
                            );

                            // console.log("_operatingCostRate:", _operatingCostRate);

                            if (_operatingCostRate) {
                                _phaseOperatingCost +=
                                    phase.spaceM2 *
                                    days *
                                    _operatingCostRate.node.costPerM2Day;
                            }
                        });
                    }

                    _totalOperatingCost += _phaseOperatingCost || 0;
                });
            }
            return _totalOperatingCost;
        },

        calEstimatedRevenue() {
            return (
                parseFloat(this.expectedSalesPrice || 0) *
                parseFloat(this.quantity || 0) *
                (1 - parseFloat(this.failureRate || 0) / 100)
            );
        },

        calProfit() {
            return (
                this.calEstimatedRevenue -
                this.calYoungPlantCost -
                this.calGrowthCost -
                this.calProductCost -
                this.calTotalOperatingCost
            );
        }
    },
    watch: {
        currentlySelectedFolder: {
            handler() {
                this.targetFolderToCopy = this.currentlySelectedFolder;
            }
        },
        dataTableOptions: {
            handler() {
                this.refreshCurrentPlantsPage();
            },
            deep: true
        },
        "plannedTask.numPhases"(val) {
            // if editing, don't process
            if (this.isLoadingPlannedTask) {
                return;
            }

            if (this.selectedTemplateNodeId === null) {
                if (val > this.plannedTask.phases.length) {
                    for (
                        var i = this.plannedTask.phases.length + 1;
                        i <= val;
                        i++
                    ) {
                        this.plannedTask.phases.push({
                            isSalesPhase: false,
                            phaseNo: i,
                            numDays: null,
                            numPlantsPerM2: null
                        });
                    }
                } else if (val < this.plannedTask.phases.length) {
                    this.plannedTask.phases.splice(
                        val,
                        this.plannedTask.phases.length - val
                    );
                }
            }
        },
        selectedStartDate(val) {
            // if editing, don't process
            if (this.isLoadingPlannedTask) {
                return;
            }

            if (val !== null) {
                // refreshPhaseDateRange
                this.refreshPhaseDateRange();
            }
        },
        salesStartDate(val) {
            if (val) {
                this.salesStartWeek = parseInt(moment(val).format("W"));
            } else {
                this.salesStartWeek = null;
            }

            // //calculate expected sales price
            // this.calculateExpectedSalesPrice();
        },
        salesEndDate(val) {
            if (val) {
                this.salesEndWeek = parseInt(moment(val).format("W"));
            } else {
                this.salesEndWeek = null;
            }

            // //calculate expected sales price
            // this.calculateExpectedSalesPrice();
        },
        startDateOption(val) {
            // if editing, don't process
            if (this.isLoadingPlannedTask) {
                return;
            }
            if (val !== null && this.selectedStartDate) {
                // refreshPhaseDateRange
                this.refreshPhaseDateRange();
            }
        },
        selectedPlant(val) {
            if (val !== null) {
                this.supplierNodeId = val.plant.defaultSupplier
                    ? val.plant.defaultSupplier.id
                    : null;
            }
            // if editing, don't process
            if (this.isLoadingPlannedTask) {
                return;
            }

            if (val !== null) {
                this.resetFormStepPhases();
            }
        },
        isImportPhasesFromTemplate() {
            // if editing, don't process
            if (this.isLoadingPlannedTask) {
                return;
            }

            this.resetFormStepPhases();
        },
        selectedTemplateNodeId(val) {
            // if editing, don't process
            if (this.isLoadingPlannedTask) {
                return;
            }

            if (val) {
                const selectedTemplate = this.selectedPlant.plant.plantGrowthTemplateSet.edges.find(
                    item => {
                        return item.node.id === val;
                    }
                );

                if (selectedTemplate !== null) {
                    this.plannedTask.sales.numDays =
                        selectedTemplate.node.maxSalesDays;
                    this.plannedTask.numPhases =
                        selectedTemplate.node.numPhases;
                    // th
                    this.getPlantGrowthTemplate(selectedTemplate.node.id);
                }

                this.$refs.formStep3.resetValidation();
            } else {
                this.resetFormStepPhases();
            }
        }
    },
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        initializeEditingPlannedTask(plannedTaskNodeId) {
            // load greenhouses for calculating operation cost
            this.$apollo.queries.greenhouses.skip = false;

            // load planned task
            this.isLoadingPlannedTask = true;

            this.step = 4;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query plannedTask($id: ID!) {
                            plannedTask(id: $id) {
                                id
                                quantity
                                numPhases
                                unitPurchasePrice
                                plantFixedCostRate
                                plantFixedCostValue
                                failureRate
                                expectedSalesPrice
                                productFixedCostRate
                                productFixedCostValue
                                supplierOrderNo
                                totalGrowthDays
                                maxSalesDays
                                growthStartDate
                                growthEndDate
                                salesStartDate
                                salesEndDate
                                note
                                planFolder {
                                    id
                                    name
                                }
                                supplier {
                                    id
                                    name
                                }
                                plant {
                                    id
                                    name
                                    defaultSupplier {
                                        id
                                        name
                                    }
                                    plantGrowthTemplateSet {
                                        edges {
                                            node {
                                                id
                                                name
                                                numPhases
                                                maxSalesDays
                                                potSize {
                                                    id
                                                    potSizeCm
                                                }
                                            }
                                        }
                                    }
                                }
                                plantGrowthTemplate {
                                    id
                                    name
                                }
                                potSize {
                                    id
                                    potSizeCm
                                }
                                color {
                                    id
                                    color
                                }
                                environment {
                                    id
                                    environment
                                }
                                tag {
                                    id
                                    tag
                                }
                                plannedTaskPhaseSet {
                                    edges {
                                        node {
                                            id
                                            phaseNo
                                            isSalesPhase
                                            numDays
                                            startDate
                                            endDate
                                            numPlantsPerM2
                                            spaceM2
                                            greenhouseDepartmentSection {
                                                id
                                                name
                                                greenhouseDepartment {
                                                    id
                                                    name
                                                    greenhouse {
                                                        id
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                plantSalesQuantityAndPriceByWeek {
                                    weekNumber
                                    avgRevenue
                                    avgSoldQuantity
                                    avgSalesPrice
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        id: plannedTaskNodeId
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result
                    // console.log(response.data);

                    this.step = 4;

                    this.updatedPlannedTaskNodeId =
                        response.data.plannedTask.id;

                    this.targetFolderToCopy = response.data.plannedTask
                        .planFolder
                        ? {
                              node: response.data.plannedTask.planFolder
                          }
                        : { node: { name: "Default", id: null } };

                    // initialize step 1: selected plant
                    this.selectedPlant = {
                        ...this.selectedPlant,
                        plant: response.data.plannedTask.plant,
                        potSize: response.data.plannedTask.potSize,
                        color: response.data.plannedTask.color,
                        environment: response.data.plannedTask.environment,
                        tag: response.data.plannedTask.tag
                    };

                    // initialize step 2: quantity and supplier
                    this.quantity = response.data.plannedTask.quantity;
                    this.note = response.data.plannedTask.note;
                    this.supplierOrderNo =
                        response.data.plannedTask.supplierOrderNo;
                    this.unitPurchasePrice =
                        response.data.plannedTask.unitPurchasePrice || 0;
                    this.plantFixedCostRate =
                        response.data.plannedTask.plantFixedCostRate.toFixed(
                            2
                        ) || 0;
                    this.failureRate =
                        response.data.plannedTask.failureRate * 100 || 0;
                    this.supplierNodeId = response.data.plannedTask.supplier
                        ? response.data.plannedTask.supplier.id
                        : null;

                    // initialize step 3: phases
                    this.selectedTemplateNodeId = response.data.plannedTask
                        .plantGrowthTemplate
                        ? response.data.plannedTask.plantGrowthTemplate.id
                        : null;

                    this.isImportPhasesFromTemplate = this
                        .selectedTemplateNodeId
                        ? true
                        : false;

                    this.selectedStartDate =
                        this.startDateOption == "growth"
                            ? response.data.plannedTask.growthStartDate
                            : response.data.plannedTask.salesStartDate;

                    // this.datePickerCurrentDate =
                    //     response.data.plannedTask.growthStartDate;

                    this.plannedTask = {
                        ...this.plannedTask,
                        numPhases: response.data.plannedTask.numPhases,
                        startDate: response.data.plannedTask.growthStartDate,
                        phases: response.data.plannedTask.plannedTaskPhaseSet.edges
                            .filter(item => {
                                return item.node.isSalesPhase === false;
                            })
                            .map(item => {
                                return {
                                    isSalesPhase: item.node.isSalesPhase,
                                    phaseNo: item.node.phaseNo,
                                    greenhouseDepartmentSectionNodeId:
                                        item.node.greenhouseDepartmentSection
                                            .id,
                                    numDays: item.node.numDays,
                                    numPlantsPerM2: item.node.numPlantsPerM2,
                                    startDate: item.node.startDate,
                                    endDate: item.node.endDate
                                };
                            }),
                        sales: {
                            isSalesPhase: true,
                            numDays: response.data.plannedTask.maxSalesDays
                        }
                    };

                    this.convertPlantSalesQuantityAndPriceByWeekToLists(
                        response.data.plannedTask
                            .plantSalesQuantityAndPriceByWeek
                    );

                    if (this.expectedSalesPrice == 0) {
                        this.expectedSalesPrice =
                            response.data.plannedTask.expectedSalesPrice || 0;
                    }
                    this.productFixedCostRate =
                        response.data.plannedTask.productFixedCostRate || 0;
                    // console.log(this.plannedTask);
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    this.isLoadingPlannedTask = false;
                });
        },

        getPlantGrowthTemplate(plantGrowthTemplateNodeId) {
            if (!plantGrowthTemplateNodeId) {
                return;
            }

            this.isLoadingPlantGrowthTemplate = true;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query plantGrowthTemplate($id: ID!) {
                            plantGrowthTemplate(id: $id) {
                                id
                                name
                                numPhases
                                seasonEffectMonths
                                totalGrowthDays
                                maxSalesDays
                                potSize {
                                    id
                                    potSizeCm
                                }
                                plant {
                                    id
                                    name
                                }
                                plantGrowthTemplatePhaseSet {
                                    edges {
                                        node {
                                            id
                                            phaseNo
                                            numDays
                                            numPlantsPerM2
                                            defaultGreenhouseDepartmentSection {
                                                id
                                                name
                                            }
                                        }
                                    }
                                }
                                createdAt
                                updatedAt
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        id: plantGrowthTemplateNodeId
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result
                    // console.log(response.data);

                    let plantGrowthTemplate = response.data.plantGrowthTemplate;

                    this.plannedTask.phases = plantGrowthTemplate.plantGrowthTemplatePhaseSet.edges.map(
                        item => {
                            return {
                                isSalesPhase: false,
                                phaseNo: item.node.phaseNo,
                                numDays: item.node.numDays,
                                numPlantsPerM2: item.node.numPlantsPerM2,
                                greenhouseDepartmentSectionNodeId: item.node
                                    .defaultGreenhouseDepartmentSection
                                    ? item.node
                                          .defaultGreenhouseDepartmentSection.id
                                    : null
                            };
                        }
                    );
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    // this.isSavingOrderPrintStatus = false;
                    this.isLoadingPlantGrowthTemplate = false;
                });
        },

        resetFormStep1() {
            this.step = 1;
            this.search = "";
            this.configingPlant = null;
            this.selectedPlant = null;
            this.supplierNodeId = null;
            this.supplierOrderNo = null;
            this.note = null;
            this.selectedTemplateNodeId = null;
            this.quantity = null;
            this.unitPurchasePrice = null;
            this.plantFixedCostRate = null;
            this.failureRate = null;
            // this.currentGreenhouseDepartmentSectionNodeId = null;
            this.currentPhaseNo = 1;
            this.isImportPhasesFromTemplate = true;
            this.selectedStartDate = null;
            // this.currentSpaceM2 = null;

            this.expectedSalesPrice = 0;
            this.salesStartWeek = null;
            this.salesEndWeek = null;
            this.salesRevenueList = [];
            this.salesVolumnList = [];
            this.salesPriceList = [];

            if (this.$refs.formStep2) {
                this.$refs.formStep2.resetValidation();
            }
        },

        resetFormStepPhases() {
            this.isSaving = false;
            this.selectedTemplateNodeId = null;
            this.plannedTask = JSON.parse(
                JSON.stringify(this.defaultPlannedTask)
            );

            if (this.$refs.formStep3) {
                this.$refs.formStep3.resetValidation();
            }
        },

        step2Next() {
            if (!this.$refs.formStep2.validate()) {
                return;
            }

            this.step = 3;
        },

        step3Next() {
            if (!this.$refs.formStep3.validate()) {
                return;
            }

            if (this.growthStartDate) {
                this.refreshPhaseDateRange();
            }

            this.step = 4;
        },

        generateTaskPhaseTickLabels(numPhases) {
            var tickLabels = [];

            var i;
            for (i = 1; i <= numPhases; i++) {
                tickLabels.push("P" + i);
            }

            tickLabels.push("Sale");

            return tickLabels;
        },

        onClearSearch() {
            this.search = "";

            this.refreshCurrentPlantsPage();
        },

        refreshCurrentPlantsPage() {
            const {
                sortBy,
                sortDesc,
                page,
                itemsPerPage
            } = this.dataTableOptions;

            var _first = itemsPerPage;
            var _last = null;
            var _before = null;
            var _after = null;
            var _orderBy = null;
            const _isArchived = false;

            if (page < this.page) {
                _first = null;
                _last = itemsPerPage;
                _before = this.tenantPlants.pageInfo.startCursor;
                _after = null;
            }

            if (page > this.page) {
                _first = itemsPerPage;
                _last = null;
                _before = null;
                _after = this.tenantPlants.pageInfo.endCursor;
            }

            if (sortBy) {
                switch (sortBy[0]) {
                    case "node.plantSpecies.name":
                        _orderBy = "plantSpeciesName";
                        break;
                    case "node.name":
                        _orderBy = "plantName";
                        break;
                    case "node.numProducts":
                        _orderBy = "numProducts";
                        break;
                    default:
                        _orderBy = null;
                        break;
                }

                if (sortDesc[0] === true) {
                    _orderBy = "-" + _orderBy;
                }
            }

            this.$apollo.queries.tenantPlants.setVariables({
                first: _first,
                last: _last,
                before: _before,
                after: _after,
                orderBy: _orderBy,
                search: this.search,
                isArchived: _isArchived
            });

            // set skip false to execute the query
            this.$apollo.queries.tenantPlants.skip = false;
            this.$apollo.queries.tenantPlants.refresh();

            // update this.page
            this.page = page;
        },

        calculateSpaceM2(numPlantsPerM2, quantity) {
            return numPlantsPerM2 > 0
                ? Math.ceil(quantity / numPlantsPerM2)
                : 0;
        },

        onConfigPlant(item) {
            this.configingPlant = item;
            this.configPlantDialog = true;
        },

        onPlantConfirmed(item) {
            // TODO: get plant sales quantity and price by week
            this.getPlantSalesQuantityAndPriceByWeekByPlantPotSize(
                item.plantPotSize.id
            );

            // this.salesStartWeek = null;
            // this.salesEndWeek = null;
            this.selectedPlant = null;
            this.selectedPlant = Object.assign({}, item);

            this.unitPurchasePrice =
                item.plantPotSize.youngPlantDefaultCost || 0;
            this.plantFixedCostRate = parseFloat(
                item.plantPotSize.plantFixedCostTemplate
                    ? item.plantPotSize.plantFixedCostTemplate.totalCost.toFixed(
                          5
                      )
                    : 0
            );
            this.failureRate = item.plantPotSize.plantFixedCostTemplate
                ? item.plantPotSize.plantFixedCostTemplate.failureRate * 100
                : 0;
            this.configPlantDialog = false;
            this.configingPlant = null;
            this.step = 2;
        },

        // refresh phase start date and end date when select task start date in date picker
        refreshPhaseDateRange() {
            var accumulateDays = 0;
            // const today = new Date();

            if (!this.selectedStartDate) {
                return;
            }

            // select growth start date
            if (this.startDateOption == "growth") {
                this.plannedTask.startDate = this.selectedStartDate;
            }
            // select sales start date
            else {
                let totalGrowthDays = this.plannedTask.phases.reduce(
                    (accumulator, item) => accumulator + parseInt(item.numDays),
                    0
                );

                this.plannedTask.startDate =
                    new Date(this.selectedStartDate) - totalGrowthDays * 864e5;
            }

            this.plannedTask.phases
                .sort((a, b) => a.phaseNo - b.phaseNo)
                .forEach(item => {
                    const _startDate =
                        this.growthStartDate.getTime() + accumulateDays * 864e5;
                    item.startDate = moment(_startDate).format("YYYY-MM-DD");

                    const _endDate = _startDate + (item.numDays - 1) * 864e5;
                    item.endDate = moment(_endDate).format("YYYY-MM-DD");

                    accumulateDays += parseInt(item.numDays);
                });

            // calculate expected sales price
            this.$nextTick(() => {
                this.calculateExpectedSalesPrice();
            });
        },

        // calendar functions
        functionAllowedDates() {
            // const calendarDate = new Date(date);
            // // // only monday is allowed to be selected
            // return calendarDate.getDay() === 1;
            // allow all days
            return true;
        },

        functionCalanderEvents(date) {
            if (this.plannedTask.startDate !== null) {
                const calendarDate = new Date(date);

                // growing
                if (
                    calendarDate >= this.growthStartDate &&
                    calendarDate <= this.growthEndDate
                ) {
                    return "green lighten-1";
                }

                // saling
                if (
                    calendarDate >= this.salesStartDate &&
                    calendarDate <= this.salesEndDate
                ) {
                    return "orange";
                }

                return false;
            } else {
                return false;
            }
        },

        saveMutation() {
            if (!this.$refs.formStep4.validate()) {
                return;
            }

            // console.log("PlanFolder ID on PlanTaskEditDialog ", this.targetFolderToCopy.node.id)
            this.isSaving = true;

            if (
                this.updatedPlannedTaskNodeId &&
                !this.createOrCopyPlanToFolderBool
            ) {
                // console.log("I am updating Plan Task")
                this.$apollo
                    .mutate({
                        mutation: gql`
                            mutation updatePlannedTask(
                                $input: UpdatePlannedTaskInput!
                            ) {
                                updatePlannedTask(input: $input) {
                                    plannedTask {
                                        id
                                        note
                                        numPhases
                                        planFolder {
                                            id
                                            name
                                        }
                                        plant {
                                            id
                                            name
                                            plantSpecies {
                                                id
                                                name
                                            }
                                        }
                                        color {
                                            id
                                            color
                                        }
                                        potSize {
                                            id
                                            potSizeCm
                                        }
                                        environment {
                                            id
                                            environment
                                        }
                                        tag {
                                            id
                                            tag
                                        }
                                        plantGrowthTemplate {
                                            id
                                            name
                                        }
                                        supplier {
                                            id
                                            name
                                        }
                                        totalGrowthDays
                                        maxSalesDays
                                        growthStartDate
                                        growthEndDate
                                        salesStartDate
                                        salesEndDate
                                        supplierOrderNo
                                        quantity
                                        unitPurchasePrice
                                        plantFixedCostRate
                                        plantFixedCostValue
                                        failureRate
                                        expectedSalesPrice
                                        productFixedCostRate
                                        productFixedCostValue
                                        currency
                                        plannedTaskPhaseSet {
                                            edges {
                                                node {
                                                    id
                                                    phaseNo
                                                    startDate
                                                    endDate
                                                    numDays
                                                    numPlantsPerM2
                                                    spaceM2
                                                    isSalesPhase
                                                }
                                            }
                                        }
                                        updatedAt
                                        updatedBy
                                    }
                                }
                            }
                        `,
                        variables: {
                            input: {
                                id: this.updatedPlannedTaskNodeId,
                                planFolderNodeId: this.targetFolderToCopy.node
                                    .id,
                                plantNodeId: this.selectedPlant.plant.id,
                                colorNodeId: this.selectedPlant.color.id,
                                potSizeNodeId: this.selectedPlant.potSize.id,
                                environmentNodeId: this.selectedPlant
                                    .environment.id,
                                tagNodeId: this.selectedPlant.tag
                                    ? this.selectedPlant.tag.id
                                    : null,
                                plantGrowthTemplateNodeId: this
                                    .selectedTemplateNodeId,
                                supplierNodeId: this.supplierNodeId,
                                numPhases: this.plannedTask.numPhases,
                                totalGrowthDays: this.totalGrowthDays,
                                maxSalesDays: this.plannedTask.sales.numDays,
                                growthStartDate: moment(
                                    this.growthStartDate
                                ).format("YYYY-MM-DD"),
                                growthEndDate: moment(
                                    this.growthEndDate
                                ).format("YYYY-MM-DD"),
                                salesStartDate: moment(
                                    this.salesStartDate
                                ).format("YYYY-MM-DD"),
                                salesEndDate: moment(this.salesEndDate).format(
                                    "YYYY-MM-DD"
                                ),
                                supplierOrderNo: this.supplierOrderNo,
                                note: this.note,
                                quantity: parseInt(this.quantity),
                                unitPurchasePrice: parseFloat(
                                    this.unitPurchasePrice
                                ),
                                plantFixedCostRate: parseFloat(
                                    this.plantFixedCostRate
                                ),
                                failureRate: parseFloat(this.failureRate) / 100,
                                expectedSalesPrice: parseFloat(
                                    this.expectedSalesPrice
                                ),
                                productFixedCostRate: parseFloat(
                                    this.productFixedCostRate
                                ),
                                currency: "EUR",
                                phases: this.plannedTaskWithSalesPhases
                            }
                        }
                    })
                    .then(response => {
                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Plan is updated!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);

                        this.isSaving = false;

                        this.$emit("item-created", response);

                        this.closeMainDialog();
                    })
                    .catch(error => {
                        //handle errors
                        console.log(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);

                        this.isSaving = false;
                    })
                    .finally();
            } else {
                this.$apollo
                    .mutate({
                        mutation: gql`
                            mutation createPlannedTask(
                                $input: CreatePlannedTaskInput!
                            ) {
                                createPlannedTask(input: $input) {
                                    plannedTask {
                                        id
                                        planFolder {
                                            id
                                            name
                                        }
                                        note
                                        numPhases
                                        planFolder {
                                            id
                                            name
                                        }
                                        plant {
                                            id
                                            name
                                            plantSpecies {
                                                id
                                                name
                                            }
                                        }
                                        color {
                                            id
                                            color
                                        }
                                        potSize {
                                            id
                                            potSizeCm
                                        }
                                        environment {
                                            id
                                            environment
                                        }
                                        tag {
                                            id
                                            tag
                                        }
                                        plantGrowthTemplate {
                                            id
                                            name
                                        }
                                        supplier {
                                            id
                                            name
                                        }
                                        totalGrowthDays
                                        maxSalesDays
                                        growthStartDate
                                        growthEndDate
                                        salesStartDate
                                        salesEndDate
                                        supplierOrderNo
                                        quantity
                                        unitPurchasePrice
                                        plantFixedCostRate
                                        plantFixedCostValue
                                        failureRate
                                        expectedSalesPrice
                                        productFixedCostRate
                                        productFixedCostValue
                                        currency
                                        plannedTaskPhaseSet {
                                            edges {
                                                node {
                                                    id
                                                    phaseNo
                                                    startDate
                                                    endDate
                                                    numDays
                                                    numPlantsPerM2
                                                    spaceM2
                                                    isSalesPhase
                                                }
                                            }
                                        }
                                        updatedAt
                                        updatedBy
                                    }
                                }
                            }
                        `,
                        variables: {
                            input: {
                                plantNodeId: this.selectedPlant.plant.id,
                                colorNodeId: this.selectedPlant.color.id,
                                planFolderNodeId: this.targetFolderToCopy.node
                                    .id,
                                potSizeNodeId: this.selectedPlant.potSize.id,
                                environmentNodeId: this.selectedPlant
                                    .environment.id,
                                tagNodeId: this.selectedPlant.tag
                                    ? this.selectedPlant.tag.id
                                    : null,
                                plantGrowthTemplateNodeId: this
                                    .selectedTemplateNodeId,
                                supplierNodeId: this.supplierNodeId,
                                numPhases: this.plannedTask.numPhases,
                                totalGrowthDays: this.totalGrowthDays,
                                maxSalesDays: this.plannedTask.sales.numDays,
                                growthStartDate: moment(
                                    this.growthStartDate
                                ).format("YYYY-MM-DD"),
                                growthEndDate: moment(
                                    this.growthEndDate
                                ).format("YYYY-MM-DD"),
                                salesStartDate: moment(
                                    this.salesStartDate
                                ).format("YYYY-MM-DD"),
                                salesEndDate: moment(this.salesEndDate).format(
                                    "YYYY-MM-DD"
                                ),
                                supplierOrderNo: this.supplierOrderNo,
                                note: this.note,
                                quantity: parseInt(this.quantity),
                                unitPurchasePrice: parseFloat(
                                    this.unitPurchasePrice
                                ),
                                plantFixedCostRate: parseFloat(
                                    this.plantFixedCostRate
                                ),
                                failureRate: parseFloat(this.failureRate) / 100,
                                expectedSalesPrice: parseFloat(
                                    this.expectedSalesPrice
                                ),
                                productFixedCostRate: parseFloat(
                                    this.productFixedCostRate
                                ),
                                currency: "EUR",
                                phases: this.plannedTaskWithSalesPhases
                            }
                        }
                    })
                    .then(response => {
                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Plan is created!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);

                        this.isSaving = false;

                        this.$emit("item-created", response);

                        this.closeMainDialog();
                    })
                    .catch(error => {
                        //handle errors
                        console.log(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .finally(() => {
                        this.$emit("setCreateOrCopyPlanFalse", false);
                    });
            }
        },
        saveMutationAndAddAnother() {
            if (!this.$refs.formStep4.validate()) {
                return;
            }

            this.isSaving = true;

            this.refreshPhaseDateRange();

            if (this.updatedPlannedTaskNodeId) {
                this.$apollo
                    .mutate({
                        mutation: gql`
                            mutation updatePlannedTask(
                                $input: UpdatePlannedTaskInput!
                            ) {
                                updatePlannedTask(input: $input) {
                                    plannedTask {
                                        id
                                        note
                                        numPhases
                                        planFolder {
                                            id
                                            name
                                        }
                                        plant {
                                            id
                                            name
                                            plantSpecies {
                                                id
                                                name
                                            }
                                        }
                                        color {
                                            id
                                            color
                                        }
                                        potSize {
                                            id
                                            potSizeCm
                                        }
                                        environment {
                                            id
                                            environment
                                        }
                                        tag {
                                            id
                                            tag
                                        }
                                        plantGrowthTemplate {
                                            id
                                            name
                                        }
                                        supplier {
                                            id
                                            name
                                        }
                                        totalGrowthDays
                                        maxSalesDays
                                        growthStartDate
                                        growthEndDate
                                        salesStartDate
                                        salesEndDate
                                        supplierOrderNo
                                        quantity
                                        unitPurchasePrice
                                        plantFixedCostRate
                                        plantFixedCostValue
                                        failureRate
                                        expectedSalesPrice
                                        productFixedCostRate
                                        productFixedCostValue
                                        currency
                                        plannedTaskPhaseSet {
                                            edges {
                                                node {
                                                    id
                                                    phaseNo
                                                    startDate
                                                    endDate
                                                    numDays
                                                    numPlantsPerM2
                                                    spaceM2
                                                    isSalesPhase
                                                }
                                            }
                                        }
                                        updatedAt
                                        updatedBy
                                    }
                                }
                            }
                        `,
                        variables: {
                            input: {
                                id: this.updatedPlannedTaskNodeId,
                                planFolderNodeId: this.targetFolderToCopy.node
                                    .id,
                                plantNodeId: this.selectedPlant.plant.id,
                                colorNodeId: this.selectedPlant.color.id,
                                potSizeNodeId: this.selectedPlant.potSize.id,
                                environmentNodeId: this.selectedPlant
                                    .environment.id,
                                tagNodeId: this.selectedPlant.tag
                                    ? this.selectedPlant.tag.id
                                    : null,
                                plantGrowthTemplateNodeId: this
                                    .selectedTemplateNodeId,
                                supplierNodeId: this.supplierNodeId,
                                numPhases: this.plannedTask.numPhases,
                                totalGrowthDays: this.totalGrowthDays,
                                maxSalesDays: this.plannedTask.sales.numDays,
                                growthStartDate: moment(
                                    this.growthStartDate
                                ).format("YYYY-MM-DD"),
                                growthEndDate: moment(
                                    this.growthEndDate
                                ).format("YYYY-MM-DD"),
                                salesStartDate: moment(
                                    this.salesStartDate
                                ).format("YYYY-MM-DD"),
                                salesEndDate: moment(this.salesEndDate).format(
                                    "YYYY-MM-DD"
                                ),
                                supplierOrderNo: this.supplierOrderNo,
                                note: this.note,
                                quantity: parseInt(this.quantity),
                                unitPurchasePrice: parseFloat(
                                    this.unitPurchasePrice
                                ),
                                plantFixedCostRate: parseFloat(
                                    this.plantFixedCostRate
                                ),
                                failureRate: parseFloat(this.failureRate) / 100,
                                expectedSalesPrice: parseFloat(
                                    this.expectedSalesPrice
                                ),
                                productFixedCostRate: parseFloat(
                                    this.productFixedCostRate
                                ),
                                currency: "EUR",
                                phases: this.plannedTaskWithSalesPhases
                            }
                        }
                    })
                    .then(response => {
                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Plan is updated!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);

                        this.isSaving = false;

                        this.$emit("item-created", response);

                        this.resetWholeDialog();
                    })
                    .catch(error => {
                        //handle errors
                        console.log(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);

                        this.isSaving = false;
                    })
                    .finally();
            } else {
                this.$apollo
                    .mutate({
                        mutation: gql`
                            mutation createPlannedTask(
                                $input: CreatePlannedTaskInput!
                            ) {
                                createPlannedTask(input: $input) {
                                    plannedTask {
                                        id
                                        planFolder {
                                            id
                                            name
                                        }
                                        note
                                        numPhases
                                        planFolder {
                                            id
                                            name
                                        }
                                        plant {
                                            id
                                            name
                                            plantSpecies {
                                                id
                                                name
                                            }
                                        }
                                        color {
                                            id
                                            color
                                        }
                                        potSize {
                                            id
                                            potSizeCm
                                        }
                                        environment {
                                            id
                                            environment
                                        }
                                        tag {
                                            id
                                            tag
                                        }
                                        plantGrowthTemplate {
                                            id
                                            name
                                        }
                                        supplier {
                                            id
                                            name
                                        }
                                        totalGrowthDays
                                        maxSalesDays
                                        growthStartDate
                                        growthEndDate
                                        salesStartDate
                                        salesEndDate
                                        supplierOrderNo
                                        quantity
                                        unitPurchasePrice
                                        plantFixedCostRate
                                        plantFixedCostValue
                                        failureRate
                                        expectedSalesPrice
                                        productFixedCostRate
                                        productFixedCostValue
                                        currency
                                        plannedTaskPhaseSet {
                                            edges {
                                                node {
                                                    id
                                                    phaseNo
                                                    startDate
                                                    endDate
                                                    numDays
                                                    numPlantsPerM2
                                                    spaceM2
                                                    isSalesPhase
                                                }
                                            }
                                        }
                                        updatedAt
                                        updatedBy
                                    }
                                }
                            }
                        `,
                        variables: {
                            input: {
                                plantNodeId: this.selectedPlant.plant.id,
                                colorNodeId: this.selectedPlant.color.id,
                                planFolderNodeId: this.targetFolderToCopy.node
                                    .id,
                                potSizeNodeId: this.selectedPlant.potSize.id,
                                environmentNodeId: this.selectedPlant
                                    .environment.id,
                                tagNodeId: this.selectedPlant.tag
                                    ? this.selectedPlant.tag.id
                                    : null,
                                plantGrowthTemplateNodeId: this
                                    .selectedTemplateNodeId,
                                supplierNodeId: this.supplierNodeId,
                                numPhases: this.plannedTask.numPhases,
                                totalGrowthDays: this.totalGrowthDays,
                                maxSalesDays: this.plannedTask.sales.numDays,
                                growthStartDate: moment(
                                    this.growthStartDate
                                ).format("YYYY-MM-DD"),
                                growthEndDate: moment(
                                    this.growthEndDate
                                ).format("YYYY-MM-DD"),
                                salesStartDate: moment(
                                    this.salesStartDate
                                ).format("YYYY-MM-DD"),
                                salesEndDate: moment(this.salesEndDate).format(
                                    "YYYY-MM-DD"
                                ),
                                supplierOrderNo: this.supplierOrderNo,
                                note: this.note,
                                quantity: parseInt(this.quantity),
                                unitPurchasePrice: parseFloat(
                                    this.unitPurchasePrice
                                ),
                                plantFixedCostRate: parseFloat(
                                    this.plantFixedCostRate
                                ),
                                failureRate: parseFloat(this.failureRate) / 100,
                                expectedSalesPrice: parseFloat(
                                    this.expectedSalesPrice
                                ),
                                productFixedCostRate: parseFloat(
                                    this.productFixedCostRate
                                ),
                                currency: "EUR",
                                phases: this.plannedTaskWithSalesPhases
                            }
                        }
                    })
                    .then(response => {
                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `Plan is created!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);

                        this.isSaving = false;

                        this.$emit("item-created", response);

                        this.resetWholeDialog();
                    })
                    .catch(error => {
                        //handle errors
                        console.log(error);

                        let _error = error.graphQLErrors
                            .map(error => error.message)
                            .join(", ");

                        // show snackbar
                        const payload = {
                            color: "error",
                            message: _error
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);

                        this.isSaving = false;
                    })
                    .finally();
            }
        },

        resetWholeDialog() {
            this.updatedPlannedTaskNodeId = null;
            this.step = 1;
            this.resetFormStep1();
            this.refreshCurrentPlantsPage();
            this.resetFormStepPhases();
            if (this.$refs.formStep2) {
                this.$refs.formStep2.resetValidation();
            }
            if (this.$refs.formStep3) {
                this.$refs.formStep3.resetValidation();
            }
            if (this.$refs.formStep4) {
                this.$refs.formStep4.resetValidation();
            }
        },

        closeMainDialog() {
            this.resetWholeDialog();
            this.$emit("update:dialog", false);
        },

        goToPlantDetail(plantNodeId) {
            // this.$router.push({
            //     name: "plant-detail",
            //     params: { id: plant.id }
            // });

            let routeData = this.$router.resolve({
                name: "plant-detail",
                params: { id: plantNodeId }
            });
            window.open(routeData.href, "_blank");
        },

        getPlantSalesQuantityAndPriceByWeekByPlantPotSize(plantPotSizeNodeId) {
            this.isLoadingPlannedTask = true;

            this.$apollo
                .query({
                    // Query
                    query: gql`
                        query plantPotSize($id: ID!) {
                            plantPotSize(id: $id) {
                                id
                                salesQuantityAndPriceByWeek {
                                    weekNumber
                                    avgRevenue
                                    avgSoldQuantity
                                    avgSalesPrice
                                }
                                avgProductFixedCostRate
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        id: plantPotSizeNodeId
                    },
                    fetchPolicy: "network-only"
                })
                .then(response => {
                    // Result

                    const _salesQuantityAndPriceByWeek =
                        response.data.plantPotSize.salesQuantityAndPriceByWeek;

                    if (_salesQuantityAndPriceByWeek) {
                        this.convertPlantSalesQuantityAndPriceByWeekToLists(
                            _salesQuantityAndPriceByWeek
                        );
                    }

                    this.productFixedCostRate = parseFloat(
                        (
                            response.data.plantPotSize
                                .avgProductFixedCostRate || 0
                        ).toFixed(5)
                    );
                    // console.log(this.plannedTask);
                })
                .catch(error => {
                    // Error
                    console.error(error);
                })
                .finally(() => {
                    this.isLoadingPlannedTask = false;
                });
        },

        convertPlantSalesQuantityAndPriceByWeekToLists(
            salesQuantityAndPriceByWeek
        ) {
            // console.log(
            //     "*****convertPlantSalesQuantityAndPriceByWeekToLists() is called!"
            // );

            this.salesRevenueList = [];
            this.salesVolumnList = [];
            this.salesPriceList = [];

            if (salesQuantityAndPriceByWeek) {
                this.weeks.forEach(i => {
                    // find

                    const a = salesQuantityAndPriceByWeek.find(e => {
                        return e.weekNumber == i;
                    });

                    this.salesRevenueList.push(a ? a.avgRevenue : null);
                    this.salesVolumnList.push(a ? a.avgSoldQuantity : null);
                    this.salesPriceList.push(
                        a ? a.avgSalesPrice.toFixed(2) : null
                    );
                });
            }
        },

        calculateExpectedSalesPrice() {
            // if no sales revenue, then no expected sales price
            if (
                helper.allAreNull(this.salesRevenueList) ||
                helper.allAreNull(this.salesVolumnList)
            ) {
                return;
            }

            if (this.salesStartWeek && this.salesEndWeek) {
                // console.log("this.salesStartWeek: ", this.salesStartWeek);
                // console.log("this.salesEndWeek: ", this.salesEndWeek);

                // console.log("this.salesRevenueList: ", this.salesRevenueList);
                // console.log("this.salesVolumnList: ", this.salesVolumnList);

                let _selectedRevenueList = [];
                let _selectedSoldQuantity = [];

                if (this.salesStartWeek <= this.salesEndWeek) {
                    _selectedRevenueList = this.salesRevenueList.slice(
                        this.salesStartWeek - 1,
                        this.salesEndWeek
                    );
                    _selectedSoldQuantity = this.salesVolumnList.slice(
                        this.salesStartWeek - 1,
                        this.salesEndWeek
                    );
                } else {
                    // revenue
                    _selectedRevenueList = _selectedRevenueList.concat(
                        this.salesRevenueList.slice(0, this.salesEndWeek)
                    );

                    _selectedRevenueList = _selectedRevenueList.concat(
                        this.salesRevenueList.slice(this.salesStartWeek - 1, 53)
                    );

                    // quantity
                    _selectedSoldQuantity = _selectedSoldQuantity.concat(
                        this.salesVolumnList.slice(0, this.salesEndWeek)
                    );

                    _selectedSoldQuantity = _selectedSoldQuantity.concat(
                        this.salesVolumnList.slice(this.salesStartWeek - 1, 53)
                    );
                }

                let _totalRevenue = _selectedRevenueList.reduce(
                    (a, b) => a + b,
                    0
                );
                let _totalQuantity = _selectedSoldQuantity.reduce(
                    (a, b) => a + b,
                    0
                );

                this.expectedSalesPrice =
                    _totalQuantity == 0
                        ? 0
                        : (_totalRevenue / _totalQuantity).toFixed(2);
            } else {
                this.expectedSalesPrice = 0;
            }
        }
    }
};
</script>

<style scoped>
.v_select_margin {
    margin-left: 17%;
    margin-right: 22%;
    background-color: white;
    color: black;
}
</style>